import { Avatar, Box, Button, IconButton, Typography } from "@mui/material";
import React from "react";
import { toast } from "react-toastify";
import { useAuth } from "../../hooks/AuthContext";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router";
import { auth } from "../../../firebase";
import Grid from "@mui/material/Grid2";
import { FormInputText } from "../../components/common/FormInputText";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Edit } from "@mui/icons-material";
import useApiMutation from "../../hooks/useApiMutation";
import {
  deleteAddress,
  editCustomerDetails,
  getCustomerDetails,
  listAllAddress,
  shopListByRegion,
  storeFullAddress,
} from "../../utilities/service";
import { updateProfileSchema } from "../../utilities/schema";
import { FormDatePicker } from "../../components/common/FormDatePicker";
import useApiQuery from "../../hooks/useApiQuery";
import { ProfileSkeleton } from "../profile/ProfileSkeleton";
import CartAddressCard from "../cart/address/CartAddressCard";
import CustomModal from "../../components/common/CustomModal";
import { AddressSkeleton } from "./AddressSkeleton";
import AddressDialog from "../cart/address/AddressDialog";
import AddIcon from "@mui/icons-material/Add";

function MyAddress() {
  const { logout, user, addressDialogOpen, setAddressDialogOpen } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [selectedAddressId, setSelectedAddressId] = React.useState(null);

  const {
    data: addressList,
    error,
    isLoading,
    refetch,
  } = useApiQuery({
    queryKey: ["listAllAddress", user?.customer_id],
    queryFn: () => listAllAddress(user?.customer_id),
  });

  const methods = useForm({
    resolver: yupResolver(updateProfileSchema),
  });
  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = methods;

  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const [day, month, year] = dateStr.split("/");
    return new Date(+year, +month - 1, +day);
  };

  const updateProfile = async (data) => {
    let param = {
      customer_id: user?.customer_id,
      ...data,
    };
    if (data?.customer_dob) {
      const dob = new Date(data?.customer_dob);
      const formattedDOB = dob.toLocaleDateString("en-GB");
      param.customer_dob = formattedDOB;
    }
    mutate(param);
  };

  const handleDeleteHandler = async () => {
    if (selectedAddressId) {
      const response = await deleteAddress(selectedAddressId);
      setSelectedAddressId(null);
      setOpen(false);
      if (!response?.error) {
        refetch();
        toast.success(response?.message);
      } else {
        toast.error(response?.message);
      }
    }
  };

  // ADD THIS FUNCTION
  const handleCloseDialog = () => {
    setAddressDialogOpen(false);
    setSelectedAddressId(null);
  };

  if (isLoading) return <AddressSkeleton />;

  return (
    <>
      <div>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontWeight: 700,
            fontSize: "20px",
            lineHeight: "150%",
            color: "var(--neutral-black)",
            textAlign: "left",
            position: "relative",
            pb: 1,
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "44px",
              height: "3px",
              borderRadius: "2px",
              background: "var(--primary-red-base)",
            },
          }}
        >
          Saved address
        </Typography>
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {addressList?.map((item, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} item key={index}>
              <CartAddressCard
                key={index}
                item={item}
                onDelete={() => {
                  setOpen(true);
                  setSelectedAddressId(item?.customer_address_id);
                }}
                onEdit={() => {
                  setAddressDialogOpen(true);
                  setSelectedAddressId(item?.customer_address_id);
                }}
              />
            </Grid>
          ))}
          <Grid size={{ xs: 12, sm: 6, md: 4 }} item>
            <Box
              elevation={0}
              sx={{
                border: "2px dashed #ccc",
                borderRadius: "8px",
                p: 2,
                textAlign: "center",
                cursor: "pointer",
                height: "100%",
                transition: "0.3s",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "150px",
                "&:hover": {
                  borderColor: "var(--primary-red-base)",
                  boxShadow: "0px 4px 16px rgba(196, 154, 39, 0.2)",
                  transform: "translateY(-2px)",
                },
              }}
              onClick={() => setAddressDialogOpen(true)}
            >
              <Box display="flex" flexDirection="column" alignItems="center">
                <AddIcon fontSize="large" sx={{ mb: 1, color: "var(--primary-red-base)" }} />
                <Typography variant="body1" fontWeight="medium">
                  Add address
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </div>

      <CustomModal
        open={open}
        setOpen={setOpen}
        title={"Delete Confirmation"}
        description={" Are you sure you want to delete this address?"}
        handleConfirm={handleDeleteHandler}
        confirmBtnLaebel={"Delete"}
        cancelBtnLabel={"Cancel"}
      />

      <AddressDialog
        open={addressDialogOpen}
        setOpen={handleCloseDialog} 
        addressListFetch={refetch}
        addressData={addressList?.find(
          (item) => item?.customer_address_id === selectedAddressId,
        )}
        selectedAddressId={selectedAddressId}
        defaultCoordinates={
          selectedAddressId
            ? {
                lat: parseFloat(
                  addressList?.find(
                    (item) => item?.customer_address_id === selectedAddressId,
                  )?.latitude || 0,
                ),
                lng: parseFloat(
                  addressList?.find(
                    (item) => item?.customer_address_id === selectedAddressId,
                  )?.longitude || 0,
                ),
              }
            : { lat: 0, lng: 0 }
        }
      />
    </>
  );
}

export default MyAddress;
